import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { useTestDataVisibility } from '../context/TestDataVisibilityContext'
import type {
  FANotifPrefRow,
  FANotifPrefsResponse,
  FANotifPrefUpdate,
  FaTag,
  FaTagCreate,
  FaTagUpdate,
  FocusArea,
  FocusAreaSettings,
  FocusAreaSettingsUpdate,
  FocusAreaWorkspaceData,
  WorkspaceTimeRange,
} from '../types/focus_area'

export function useFocusAreas() {
  return useQuery<FocusArea[]>({
    queryKey: ['focus-areas'],
    queryFn: async () => {
      const res = await api.get('/focus-areas')
      return res.data
    },
    staleTime: 5 * 60_000,
  })
}

export function useFocusAreaSettings(code: string | undefined) {
  return useQuery<FocusAreaSettings>({
    queryKey: ['fa-settings', code],
    queryFn: async () => {
      const res = await api.get(`/focus-areas/${code}/settings`)
      return res.data
    },
    enabled: !!code,
  })
}

export function useUpdateFocusAreaSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ code, body }: { code: string; body: FocusAreaSettingsUpdate }) => {
      const res = await api.patch(`/focus-areas/${code}/settings`, body)
      return res.data as FocusAreaSettings
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['fa-settings', vars.code] })
      qc.invalidateQueries({ queryKey: ['fa-workspace', vars.code] })
    },
  })
}

// ── Per-FA custom tags ───────────────────────────────────────────────────────

export function useFaTags(code: string | undefined, includeInactive = false) {
  return useQuery<FaTag[]>({
    queryKey: ['fa-tags', code, includeInactive],
    queryFn: async () => {
      const res = await api.get(`/focus-areas/${code}/tags`, {
        params: includeInactive ? { include_inactive: true } : undefined,
      })
      return res.data
    },
    enabled: !!code,
    staleTime: 60_000,
  })
}

export function useCreateFaTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ code, body }: { code: string; body: FaTagCreate }) => {
      const res = await api.post(`/focus-areas/${code}/tags`, body)
      return res.data as FaTag
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['fa-tags', vars.code] })
    },
  })
}

export function useUpdateFaTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      code,
      tagId,
      body,
    }: {
      code: string
      tagId: number
      body: FaTagUpdate
    }) => {
      const res = await api.patch(`/focus-areas/${code}/tags/${tagId}`, body)
      return res.data as FaTag
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['fa-tags', vars.code] })
    },
  })
}

export function useDeleteFaTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ code, tagId, hard }: { code: string; tagId: number; hard?: boolean }) => {
      const res = await api.delete(`/focus-areas/${code}/tags/${tagId}`, {
        params: hard ? { hard: true } : undefined,
      })
      return res.data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['fa-tags', vars.code] })
    },
  })
}

export function useFocusAreaWorkspace(
  code: string | undefined,
  timeRange: WorkspaceTimeRange = 'quarter',
) {
  const { showTestData } = useTestDataVisibility()
  return useQuery<FocusAreaWorkspaceData>({
    queryKey: ['fa-workspace', code, timeRange, showTestData],
    queryFn: async () => {
      const res = await api.get(`/focus-areas/${code}/workspace`, {
        params: { time_range: timeRange, include_test: showTestData },
      })
      return res.data
    },
    enabled: !!code,
    staleTime: 30_000,
  })
}

export function useFANotifPrefs(code: string | undefined) {
  return useQuery<FANotifPrefsResponse>({
    queryKey: ['fa-notif-prefs', code],
    queryFn: async () => {
      const res = await api.get(`/focus-areas/${code}/notification-preferences`)
      return res.data
    },
    enabled: !!code,
  })
}

export function useUpdateFANotifPref() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      code,
      eventKey,
      body,
    }: {
      code: string
      eventKey: string
      body: FANotifPrefUpdate
    }) => {
      const res = await api.put(
        `/focus-areas/${code}/notification-preferences/${eventKey}`,
        body,
      )
      return res.data as FANotifPrefRow
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['fa-notif-prefs', vars.code] })
    },
  })
}

export function useMuteAllFANotifs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (code: string) => {
      const res = await api.post(`/focus-areas/${code}/notification-preferences/mute-all`)
      return res.data as FANotifPrefsResponse
    },
    onSuccess: (_data, code) => {
      qc.invalidateQueries({ queryKey: ['fa-notif-prefs', code] })
    },
  })
}

export function useResetFANotifsToDefaults() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (code: string) => {
      const res = await api.post(
        `/focus-areas/${code}/notification-preferences/reset-to-defaults`,
      )
      return res.data as FANotifPrefsResponse
    },
    onSuccess: (_data, code) => {
      qc.invalidateQueries({ queryKey: ['fa-notif-prefs', code] })
    },
  })
}
